/**
 * Lire un montant tapé par un humain, et l'écrire pour qu'il le relise.
 *
 * ## Le défaut que ce module corrige
 *
 * Signalé le 19 septembre 2026, sur les cases « À affecter » de la
 * répartition :
 *
 *   « On a un problème de saisie dans les champs des répartitions. Quand je
 *     saisis 30 il saisit 3,01 ; je dois manuellement retirer le 0,1 et
 *     remettre un 0 après le trois. »
 *
 * La case était un `<input type="number">` **contrôlé et reformaté à chaque
 * frappe** :
 *
 *     value={parts[id] ? (parts[id] / 100).toFixed(2) : ''}
 *     onChange={(e) => setParts({ ...parts, [id]: Math.round(parseFloat(e.target.value) * 100) })}
 *
 * Taper `3` produisait donc l'état `300`, que React réécrivait aussitôt dans
 * la case sous la forme `3.00`. Le curseur repart alors **à la fin** — un
 * `input[type=number]` n'expose pas sa sélection, rien ne peut la restaurer —
 * et le `0` suivant tombe derrière les décimales au lieu de tomber derrière le
 * `3`. **Une case qui réécrit ce qu'on vient d'y taper ne peut pas être
 * remplie.**
 *
 * Le `type="number"` ajoutait un second piège, silencieux celui-là : la
 * molette et les flèches modifient la valeur d'un `step`, ici `0,01`. Un
 * défilement de page au-dessus d'une case ayant le focus change donc le
 * montant d'un centime, sans un geste de saisie — et c'est très exactement le
 * `+0,01` rapporté.
 *
 * ## Ce que ce module fait
 *
 * Il sépare **ce qui est tapé** de **ce qui est compris**. La case garde le
 * texte de l'utilisateur tel quel tant qu'il écrit ; ce module le traduit en
 * centimes à côté, et le remet en forme seulement quand la case perd le focus.
 *
 * Il accepte ce qu'un Belge tape réellement : la virgule comme séparateur
 * décimal, le point ou l'espace comme séparateur de milliers, un `€` collé au
 * nombre.
 */

/**
 * Des centimes depuis ce qu'un humain a tapé, ou `null` si ce n'est pas un
 * montant.
 *
 * `null` n'est pas zéro : `null` veut dire « cette saisie ne se lit pas »,
 * zéro veut dire « zéro euro ». Les confondre ferait écrire une ligne à 0,00 €
 * là où quelqu'un a tapé de travers.
 *
 * ## Le séparateur décimal est le DERNIER
 *
 * `1.234,56` et `1,234.56` désignent le même montant dans deux conventions.
 * Plutôt que de deviner la convention, on lit la dernière virgule ou le dernier
 * point comme le séparateur décimal, et tout ce qui précède comme des
 * séparateurs de milliers. C'est vrai dans les deux conventions, donc il n'y a
 * rien à deviner.
 */
export function centimesSaisis(texte: string | null | undefined): number | null {
  /* Espaces de toutes sortes, dont l’insécable fine que produit un
     copier-coller depuis un tableur. */
  const sansEspaces = (texte ?? '').replace(/[\s\u00a0\u202f]/g, '');
  /*
    Le symbole monétaire n’est retiré qu’AUX EXTRÉMITÉS. Le retirer partout
    lirait `30\u20ac30` comme 3 030,00 € — inventer un montant à partir d’une
    faute de frappe est pire que la refuser.
  */
  const brut = sansEspaces.replace(/^\u20ac+/, '').replace(/\u20ac+$/, '');
  if (brut === '') return null;

  const dernier = Math.max(brut.lastIndexOf(','), brut.lastIndexOf('.'));
  const partieEntiere = (dernier >= 0 ? brut.slice(0, dernier) : brut).replace(/[.,]/g, '');
  const decimales = dernier >= 0 ? brut.slice(dernier + 1) : '';

  if (!/^-?\d*$/.test(partieEntiere)) return null;
  if (!/^\d*$/.test(decimales)) return null;

  const chiffresEntiers = partieEntiere.replace('-', '');
  /* Ni chiffre avant, ni chiffre après : `-`, `,` ou `€` tout seuls. */
  if (chiffresEntiers === '' && decimales === '') return null;

  const signe = partieEntiere.startsWith('-') ? -1 : 1;
  const unites = Number(chiffresEntiers || '0');
  /*
    Les décimales passent par une fraction plutôt que par un `padEnd(2)` : cela
    ARRONDIT au centime au lieu de tronquer. Un collage de `33,339` vaut
    33,34 € et non 33,33 € — tronquer ferait disparaître un centime sans le
    dire, ce qui est précisément ce qu'une comptabilité ne pardonne pas.
  */
  const centimes = Math.round(Number(`0.${decimales || '0'}`) * 100);
  return signe * (unites * 100 + centimes);
}

/**
 * L'écriture d'un montant connu, telle qu'on la remet dans une case.
 *
 * Virgule décimale, deux décimales toujours, aucun séparateur de milliers :
 * ce texte doit pouvoir être relu par `centimesSaisis` sans perte, et un
 * séparateur de milliers n'aiderait personne sur des montants de trois
 * chiffres.
 */
export function saisieDepuisCentimes(centimes: number): string {
  return (centimes / 100).toFixed(2).replace('.', ',');
}
